import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal } from 'reactstrap'
import { useForm } from 'react-hook-form'
import { useToasts } from 'react-toast-notifications'
import userApi from '_api/user'
import { getUserInfo } from '_utils/localData'

const PRESET_LABELS = ['Home', 'Office']

const AddAddressModal = ({
  isOpen,
  toggle,
  onAddressAdded,
  phoneNumber,
  hasNoAddresses = false,
}) => {
  const { addToast } = useToasts()
  const userInfo = getUserInfo() || {}

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      name: 'Home',
      address: '',
      postalCode: '',
      phone: phoneNumber || userInfo.phone || '',
      consignee: userInfo.name || '',
      isDefault: hasNoAddresses,
    },
  })

  useEffect(() => {
    if (isOpen) {
      reset({
        name: 'Home',
        address: '',
        postalCode: '',
        phone: phoneNumber || userInfo.phone || '',
        consignee: userInfo.name || '',
        isDefault: hasNoAddresses,
      })
    }
  }, [isOpen, phoneNumber, userInfo.phone, userInfo.name, hasNoAddresses, reset])

  const currentName = watch('name') || ''
  const isDefault = watch('isDefault')

  const onSubmit = async (values) => {
    try {
      const dataToSend = {
        name: (values.name || '').trim(),
        consignee: (values.consignee || '').trim(),
        postalCode: (values.postalCode || '').trim(),
        address: {
          street: (values.address || '').trim(),
        },
        country: 'Singapore',
        phone: (values.phone || '').trim(),
        isDefault: Boolean(values.isDefault),
      }

      await userApi.createUserAddress(dataToSend)

      addToast('Address added successfully!', {
        appearance: 'success',
        autoDismiss: true,
      })

      if (onAddressAdded) {
        await onAddressAdded()
      }
      toggle()
    } catch (err) {
      addToast(err?.msgResp || 'Failed to add address', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  const handlePresetClick = (label) => {
    setValue('name', label, { shouldValidate: true, shouldDirty: true })
  }

  const handleToggleDefault = () => {
    setValue('isDefault', !isDefault, { shouldDirty: true })
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className='userv2-modal-dialog'
      contentClassName='userv2-modal-content'
    >
      <div className='userv2-modal-header'>
        <div className='userv2-modal-header-left'>
          <div className='userv2-modal-icon'>
            <svg
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#009A97'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' />
              <circle cx='12' cy='10' r='3' />
            </svg>
          </div>
          <div>
            <h5 className='userv2-modal-title'>Add Delivery Address</h5>
            <p className='userv2-modal-subtitle'>
              Enter your delivery location and contact details
            </p>
          </div>
        </div>
        <button
          type='button'
          className='userv2-modal-close'
          onClick={toggle}
          aria-label='Close'
        >
          <svg
            width='14'
            height='14'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <line x1='18' y1='6' x2='6' y2='18' />
            <line x1='6' y1='6' x2='18' y2='18' />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className='userv2-modal-body'>
        {/* Address Label + Presets */}
        <div className='userv2-form-group'>
          <div className='userv2-form-label-row'>
            <label className='userv2-form-label'>
              Address Label <span className='required'>*</span>
            </label>
            <div className='userv2-preset-chips'>
              {PRESET_LABELS.map((label) => (
                <button
                  key={label}
                  type='button'
                  className={`userv2-chip ${
                    currentName.toLowerCase() === label.toLowerCase() ? 'active' : ''
                  }`}
                  onClick={() => handlePresetClick(label)}
                >
                  {label === 'Home' && (
                    <svg
                      width='11'
                      height='11'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <path d='M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z' />
                    </svg>
                  )}
                  {label === 'Office' && (
                    <svg
                      width='11'
                      height='11'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <rect x='2' y='7' width='20' height='14' rx='2' />
                      <path d='M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16' />
                    </svg>
                  )}
                  {label}
                </button>
              ))}
            </div>
          </div>
          <input
            type='text'
            className={`userv2-input ${errors.name ? 'has-error' : ''}`}
            placeholder='e.g. Home, Office, Warehouse'
            {...register('name', {
              required: 'Address label is required',
              validate: (val) => (val || '').trim().length > 0 || 'Address label cannot be empty',
            })}
          />
          {errors.name && (
            <span className='userv2-field-error'>{errors.name.message}</span>
          )}
        </div>

        {/* 2 columns: Consignee & Phone */}
        <div className='userv2-form-row'>
          <div className='userv2-form-group flex-1'>
            <label className='userv2-form-label'>Recipient Name</label>
            <input
              type='text'
              className='userv2-input'
              placeholder='e.g. Alex Tan'
              {...register('consignee')}
            />
          </div>

          <div className='userv2-form-group flex-1'>
            <label className='userv2-form-label'>Contact Phone</label>
            <input
              type='tel'
              className='userv2-input'
              placeholder='e.g. 9123 4567'
              {...register('phone')}
            />
          </div>
        </div>

        <div className='userv2-form-group'>
          <label className='userv2-form-label'>
            Street & Unit Number <span className='required'>*</span>
          </label>
          <textarea
            className={`userv2-textarea ${errors.address ? 'has-error' : ''}`}
            rows='3'
            placeholder='e.g. 123 Orchard Road, #05-12, Lucky Plaza'
            {...register('address', {
              required: 'Street & unit number is required',
              validate: (val) => (val || '').trim().length > 0 || 'Address cannot be empty',
            })}
          />
          {errors.address && (
            <span className='userv2-field-error'>{errors.address.message}</span>
          )}
        </div>

        <div className='userv2-form-group'>
          <label className='userv2-form-label'>
            Postal Code (Singapore) <span className='required'>*</span>
          </label>
          <input
            type='text'
            maxLength='6'
            className={`userv2-input ${errors.postalCode ? 'has-error' : ''}`}
            placeholder='e.g. 238858'
            {...register('postalCode', {
              required: 'Postal code is required',
              pattern: {
                value: /^\d{6}$/,
                message: 'Postal code must be exactly 6 digits',
              },
            })}
          />
          {errors.postalCode && (
            <span className='userv2-field-error'>{errors.postalCode.message}</span>
          )}
        </div>

        <div
          className={`userv2-default-toggle-card ${isDefault ? 'is-active' : ''}`}
          onClick={handleToggleDefault}
        >
          <div className='toggle-card-info'>
            <div className='toggle-card-title'>Set as primary default address</div>
            <div className='toggle-card-desc'>
              Automatically preselect this address for upcoming deliveries
            </div>
          </div>
          <div className={`userv2-toggle-switch ${isDefault ? 'on' : ''}`}>
            <div className='toggle-thumb' />
          </div>
        </div>

        {/* Modal actions */}
        <div className='userv2-modal-footer'>
          <div />

          <div className='userv2-footer-right'>
            <button
              type='button'
              className='userv2-btn-cancel'
              onClick={toggle}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='userv2-btn-save'
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Address'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

AddAddressModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onAddressAdded: PropTypes.func,
  phoneNumber: PropTypes.string,
  hasNoAddresses: PropTypes.bool,
}

export default AddAddressModal
